'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import { useRouter, useSearchParams } from 'next/navigation';
import { useAuth } from '@/context/AuthContext';
import { apiFetch } from '@/lib/api/client';
import {
  getCheckoutReturnMessage,
  mapGuestCheckoutError,
  parseCheckoutReturnState,
} from '@/lib/booking/checkoutReturn';
import { formatLessonDate } from '@/lib/booking/format';
import type {
  BookableSlot,
  CreditBalance,
  LessonType,
  Location,
  PackageProduct,
} from '@/lib/booking/types';

type BootstrapResponse = {
  locations: Location[];
  lessonTypes: LessonType[];
  slots: BookableSlot[];
  creditBalances: CreditBalance[];
};

type CheckoutUiStatus = 'success' | 'cancelled' | 'failed' | null;
type CheckoutResolution = 'idle' | 'processing' | 'confirmed' | 'failed';

function getWeekStart(dateIso: string): Date {
  const date = new Date(dateIso);
  const day = date.getDay();
  const diffToMonday = (day + 6) % 7;
  date.setDate(date.getDate() - diffToMonday);
  date.setHours(0, 0, 0, 0);
  return date;
}

export default function BookingPageClient() {
  const { session, user, profile, loading: authLoading } = useAuth();
  const router = useRouter();
  const searchParams = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [data, setData] = useState<BootstrapResponse>({
    locations: [],
    lessonTypes: [],
    slots: [],
    creditBalances: [],
  });
  const [selectedLocationId, setSelectedLocationId] = useState<string>('');
  const [selectedLessonTypeId, setSelectedLessonTypeId] = useState<string>('');
  const [selectedSlotId, setSelectedSlotId] = useState<string>('');
  const [guestName, setGuestName] = useState('');
  const [guestEmail, setGuestEmail] = useState('');
  const [guestPhone, setGuestPhone] = useState('');
  const [guestPaymentMethod, setGuestPaymentMethod] = useState<'stripe' | 'cash'>('stripe');
  const [packageCatalog, setPackageCatalog] = useState<PackageProduct[]>([]);
  const [showPackageModal, setShowPackageModal] = useState(false);
  const [checkoutReturnHandled, setCheckoutReturnHandled] = useState(false);
  const [checkoutStatus, setCheckoutStatus] = useState<CheckoutUiStatus>(null);
  const [checkoutOrderId, setCheckoutOrderId] = useState<string | null>(null);
  const [checkoutResolution, setCheckoutResolution] = useState<CheckoutResolution>('idle');
  const [checkoutBookingId, setCheckoutBookingId] = useState<string | null>(null);

  const refresh = async () => {
    setLoading(true);
    setError(null);
    try {
      const [next, packages] = await Promise.all([
        apiFetch<BootstrapResponse>('/api/book/bootstrap', {
          method: 'GET',
          session,
        }),
        apiFetch<{ packages: PackageProduct[] }>('/api/packages', {
          method: 'GET',
          session,
        }),
      ]);
      setData(next);
      setPackageCatalog(packages.packages);
      if (!selectedLocationId && next.locations[0]) {
        setSelectedLocationId(next.locations[0].id);
      }
      if (!selectedLessonTypeId && next.lessonTypes[0]) {
        setSelectedLessonTypeId(next.lessonTypes[0].id);
      }
    } catch (refreshError) {
      const message = refreshError instanceof Error ? refreshError.message : 'Failed loading slots';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!authLoading && user && profile?.role === 'admin') {
      router.replace('/admin');
      return;
    }
    if (!authLoading) {
      refresh();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, profile?.role, session?.access_token, user?.id]);

  useEffect(() => {
    if (checkoutReturnHandled) {
      return;
    }

    const returnState = parseCheckoutReturnState(searchParams);
    const returnMessage = getCheckoutReturnMessage(returnState);

    if (returnState.locationId) {
      setSelectedLocationId(returnState.locationId);
    }
    if (returnState.lessonTypeId) {
      setSelectedLessonTypeId(returnState.lessonTypeId);
    }
    if (returnState.slotId) {
      setSelectedSlotId(returnState.slotId);
    }

    if (returnMessage.kind === 'success' && returnMessage.message) {
      setSuccess(returnMessage.message);
      setError(null);
      void refresh();
    } else if (returnMessage.kind === 'error' && returnMessage.message) {
      setError(returnMessage.message);
      setSuccess(null);
    }

    if (returnState.status) {
      setCheckoutStatus(returnState.status);
      setCheckoutOrderId(returnState.orderId);
      router.replace('/book', { scroll: false });
    }
    setCheckoutReturnHandled(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkoutReturnHandled, router, searchParams]);

  useEffect(() => {
    if (checkoutStatus !== 'success' || !checkoutOrderId) {
      return;
    }

    let cancelled = false;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    let attempts = 0;
    const maxAttempts = 8;

    const pollCheckoutStatus = async () => {
      attempts += 1;
      setCheckoutResolution('processing');
      try {
        const response = await apiFetch<{
          status: 'processing' | 'confirmed' | 'failed';
          bookingId: string | null;
          message: string;
        }>(`/api/bookings/checkout-status?order_id=${encodeURIComponent(checkoutOrderId)}`, {
          method: 'GET',
          session,
        });

        if (cancelled) {
          return;
        }

        if (response.status === 'confirmed') {
          setCheckoutResolution('confirmed');
          setCheckoutBookingId(response.bookingId);
          setSuccess(
            response.bookingId
              ? `Booking confirmed. Reference: ${response.bookingId}`
              : 'Booking confirmed successfully.'
          );
          setError(null);
          void refresh();
          return;
        }

        if (response.status === 'failed') {
          setCheckoutResolution('failed');
          setError('Payment was captured but booking finalization failed. Please contact support.');
          setSuccess(null);
          return;
        }

        if (attempts < maxAttempts) {
          timeoutId = setTimeout(() => {
            void pollCheckoutStatus();
          }, 2500);
        }
      } catch (statusError) {
        if (cancelled) {
          return;
        }
        if (attempts < maxAttempts) {
          timeoutId = setTimeout(() => {
            void pollCheckoutStatus();
          }, 2500);
          return;
        }
        const message =
          statusError instanceof Error
            ? statusError.message
            : 'Unable to confirm your booking status.';
        setCheckoutResolution('failed');
        setError(message);
      }
    };

    void pollCheckoutStatus();

    return () => {
      cancelled = true;
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkoutOrderId, checkoutStatus, session?.access_token]);

  if (!authLoading && user && profile?.role === 'admin') {
    return (
      <main className="min-h-screen bg-slate-50 px-4 py-16">
        <div className="mx-auto max-w-xl rounded-2xl border border-border bg-white p-6">
          <h1 className="font-heading text-3xl font-bold text-primary">Admin session</h1>
          <p className="mt-2 text-sm text-foreground/70">
            Admin accounts operate through the admin dashboard only. Redirecting now.
          </p>
        </div>
      </main>
    );
  }

  const filteredSlots = data.slots.filter((slot) => {
    const locationOk = selectedLocationId ? slot.location_id === selectedLocationId : true;
    const lessonOk = selectedLessonTypeId ? slot.lesson_type_id === selectedLessonTypeId : true;
    return locationOk && lessonOk;
  });
  const groupedSlots = filteredSlots.reduce<
    Array<{ weekKey: string; weekLabel: string; slots: BookableSlot[] }>
  >((groups, slot) => {
    const weekStart = getWeekStart(slot.starts_at);
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekEnd.getDate() + 6);
    const weekKey = weekStart.toISOString();
    const weekLabel = `${weekStart.toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
    })} - ${weekEnd.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`;

    const existing = groups.find((group) => group.weekKey === weekKey);
    if (existing) {
      existing.slots.push(slot);
    } else {
      groups.push({ weekKey, weekLabel, slots: [slot] });
    }
    return groups;
  }, []);

  const selectedLessonType = data.lessonTypes.find(
    (lessonType) => lessonType.id === selectedLessonTypeId
  );
  const selectedCreditBalance = data.creditBalances.find(
    (balance) => balance.lesson_type_id === selectedLessonTypeId
  );
  const selectedDurationPackages = packageCatalog.filter(
    (entry) => entry.duration_minutes === selectedLessonType?.duration_minutes
  );

  const handleBook = async () => {
    if (!selectedSlotId) {
      setError('Choose a slot before booking');
      return;
    }
    if (!user && (!guestName || !guestEmail || !guestPhone.trim())) {
      setError('Guest name, email, and phone are required');
      return;
    }
    if (user && !profile?.phone?.trim()) {
      setError('A phone number is required before booking. Please update your account profile.');
      return;
    }

    setSubmitting(true);
    setError(null);
    setSuccess(null);

    try {
      if (!user) {
        const guestCheckout = await apiFetch<{
          mode: 'stripe' | 'cash';
          checkoutUrl?: string;
          bookingId?: string;
          orderId: string;
        }>('/api/checkout/guest-single', {
          method: 'POST',
          session,
          body: JSON.stringify({
            slotId: selectedSlotId,
            guestName,
            guestEmail,
            guestPhone: guestPhone.trim(),
            paymentMethod: guestPaymentMethod,
          }),
        });

        if (guestCheckout.mode === 'stripe' && guestCheckout.checkoutUrl) {
          window.location.assign(guestCheckout.checkoutUrl);
          return;
        }

        setSuccess(`Booking created successfully. Reference: ${guestCheckout.bookingId}`);
      } else {
        const availableCredits = selectedCreditBalance?.balance ?? 0;
        const requiredCredits = selectedLessonType?.credits_required ?? 1;
        if (availableCredits < requiredCredits) {
          setShowPackageModal(true);
          return;
        }

        const response = await apiFetch<{ bookingId: string }>('/api/bookings', {
          method: 'POST',
          session,
          body: JSON.stringify({
            slotId: selectedSlotId,
          }),
        });
        setSuccess(`Booking confirmed. Reference: ${response.bookingId}`);
      }
      setSelectedSlotId('');
      await refresh();
    } catch (bookingError) {
      const message =
        bookingError instanceof Error ? bookingError.message : 'Failed to create booking';
      setError(!user ? mapGuestCheckoutError(message) : message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleBuyPackage = async (packageProductId: string) => {
    setSubmitting(true);
    setError(null);
    try {
      const response = await apiFetch<{ checkoutUrl: string }>('/api/checkout/package', {
        method: 'POST',
        session,
        body: JSON.stringify({
          packageProductId,
        }),
      });
      window.location.assign(response.checkoutUrl);
    } catch (purchaseError) {
      const message =
        purchaseError instanceof Error ? purchaseError.message : 'Failed to start checkout';
      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  const renderPackageCard = (entry: PackageProduct) => (
    <div key={entry.id} className="rounded-lg border border-border p-3">
      <p className="text-sm font-semibold text-foreground">{entry.name}</p>
      <p className="mt-1 text-lg font-bold text-primary">
        EUR {(entry.price_cents / 100).toFixed(2)}
      </p>
      <p className="text-xs text-foreground/60">
        {entry.credits_granted} credits | {entry.duration_minutes} min sessions
      </p>
      {entry.package_size === 10 && (
        <p className="mt-1 text-xs text-foreground/70">
          Includes personal development plan and video analysis.
        </p>
      )}
      <button
        type="button"
        onClick={() => handleBuyPackage(entry.id)}
        className="mt-3 w-full rounded-md bg-primary px-3 py-1.5 text-xs font-semibold text-white"
      >
        Buy package
      </button>
    </div>
  );

  return (
    <main className="min-h-screen bg-slate-50 px-4 py-10 md:px-8">
      <div className="mx-auto max-w-5xl space-y-6">
        <section className="rounded-2xl border border-border bg-white p-6">
          <h1 className="font-heading text-3xl font-bold text-primary">Book a lesson</h1>
          <p className="mt-2 text-sm text-foreground/70">
            Book as guest or sign in to use duration-specific package credits.
          </p>
          <div className="mt-3 flex flex-wrap gap-3 text-sm">
            {user ? (
              <>
                <span className="rounded-full bg-green-100 px-3 py-1 text-green-800">
                  Signed in
                </span>
                <Link href="/account" className="font-medium text-primary underline">
                  Go to account
                </Link>
              </>
            ) : (
              <>
                <Link
                  href="/auth/sign-in?next=/book"
                  className="font-medium text-primary underline"
                >
                  Sign in
                </Link>
                <Link
                  href="/auth/sign-up?next=/book"
                  className="font-medium text-primary underline"
                >
                  Create account
                </Link>
              </>
            )}
          </div>
        </section>

        <section className="grid gap-6 md:grid-cols-2">
          <div className="rounded-2xl border border-border bg-white p-6">
            <h2 className="font-heading text-xl font-semibold text-primary">
              1) Select your lesson
            </h2>
            <div className="mt-4 space-y-4">
              <label className="block text-sm">
                <span className="mb-1 block font-medium text-foreground">Location</span>
                <select
                  value={selectedLocationId}
                  onChange={(event) => setSelectedLocationId(event.target.value)}
                  className="w-full rounded-lg border border-border px-3 py-2"
                >
                  {data.locations.map((location) => (
                    <option key={location.id} value={location.id}>
                      {location.name}
                    </option>
                  ))}
                </select>
              </label>
              <label className="block text-sm">
                <span className="mb-1 block font-medium text-foreground">Lesson type</span>
                <select
                  value={selectedLessonTypeId}
                  onChange={(event) => setSelectedLessonTypeId(event.target.value)}
                  className="w-full rounded-lg border border-border px-3 py-2"
                >
                  {data.lessonTypes.map((lessonType) => (
                    <option key={lessonType.id} value={lessonType.id}>
                      {lessonType.name} ({lessonType.duration_minutes} min)
                    </option>
                  ))}
                </select>
              </label>
              {selectedLessonType && (
                <p className="text-sm text-foreground/70">
                  This lesson uses <strong>{selectedLessonType.credits_required}</strong> credit(s)
                  from the <strong>{selectedLessonType.name}</strong> package.
                </p>
              )}
              {user && selectedCreditBalance && (
                <p className="text-sm text-foreground/70">
                  Available credits for this duration:{' '}
                  <strong>{selectedCreditBalance.balance}</strong>
                </p>
              )}
            </div>
          </div>

          <div className="rounded-2xl border border-border bg-white p-6">
            <h2 className="font-heading text-xl font-semibold text-primary">
              2) Select an open slot
            </h2>
            <div className="mt-4 max-h-72 space-y-2 overflow-y-auto pr-1">
              {filteredSlots.length === 0 && (
                <p className="text-sm text-foreground/60">
                  No available slots for the current selection.
                </p>
              )}
              {groupedSlots.map((group) => (
                <div key={group.weekKey} className="space-y-2">
                  <p className="text-xs font-semibold uppercase tracking-wide text-foreground/60">
                    Week of {group.weekLabel}
                  </p>
                  {group.slots.map((slot) => (
                    <button
                      type="button"
                      key={slot.id}
                      onClick={() => setSelectedSlotId(slot.id)}
                      className={`w-full rounded-lg border px-3 py-2 text-left text-sm ${
                        selectedSlotId === slot.id
                          ? 'border-primary bg-primary/5'
                          : 'border-border hover:border-primary/40'
                      }`}
                    >
                      {formatLessonDate(slot.starts_at)}
                    </button>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </section>

        {!user && (
          <section className="rounded-2xl border border-border bg-white p-6">
            <h2 className="font-heading text-xl font-semibold text-primary">3) Guest details</h2>
            <div className="mt-4 grid gap-4 md:grid-cols-3">
              <label className="text-sm">
                <span className="mb-1 block font-medium text-foreground">Full name</span>
                <input
                  value={guestName}
                  onChange={(event) => setGuestName(event.target.value)}
                  required
                  className="w-full rounded-lg border border-border px-3 py-2 text-sm"
                />
              </label>
              <label className="text-sm">
                <span className="mb-1 block font-medium text-foreground">Email</span>
                <input
                  type="email"
                  value={guestEmail}
                  onChange={(event) => setGuestEmail(event.target.value)}
                  required
                  className="w-full rounded-lg border border-border px-3 py-2 text-sm"
                />
              </label>
              <label className="text-sm">
                <span className="mb-1 block font-medium text-foreground">Phone</span>
                <input
                  value={guestPhone}
                  onChange={(event) => setGuestPhone(event.target.value)}
                  required
                  className="w-full rounded-lg border border-border px-3 py-2 text-sm"
                />
              </label>
            </div>
          </section>
        )}

        <section className="rounded-2xl border border-border bg-white p-6">
          <h2 className="font-heading text-xl font-semibold text-primary">4) Confirm booking</h2>
          <p className="mt-2 text-sm text-foreground/70">
            {user
              ? 'If you do not have enough credits, choose a package and complete payment to top up instantly.'
              : 'Guests can pay online (iDEAL/card) or select cash payment at the session.'}
          </p>
          {user && !profile?.phone?.trim() && (
            <p className="mt-3 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-700">
              A phone number is required to confirm bookings. Please update your account phone
              number before continuing.
            </p>
          )}
          {checkoutStatus === 'success' && (
            <div className="mt-3 rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800">
              <p className="font-semibold">
                {checkoutResolution === 'confirmed'
                  ? 'Booking confirmed successfully.'
                  : 'Payment return received successfully.'}
              </p>
              {checkoutResolution === 'confirmed' ? (
                <p>Your booking is now confirmed and added to your account records.</p>
              ) : checkoutResolution === 'failed' ? (
                <p>
                  We received your return from Stripe but could not complete booking finalization
                  automatically.
                </p>
              ) : (
                <p>
                  Your booking is being confirmed. We are checking payment finalization status now.
                </p>
              )}
              {checkoutOrderId && <p className="mt-1">Order reference: {checkoutOrderId}</p>}
              {checkoutBookingId && <p className="mt-1">Booking reference: {checkoutBookingId}</p>}
            </div>
          )}
          {(checkoutStatus === 'cancelled' || checkoutStatus === 'failed') && (
            <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
              <p className="font-semibold">Payment was not completed.</p>
              <p>Select another slot or payment method to continue your booking.</p>
            </div>
          )}
          {!user && (
            <div className="mt-3 inline-flex rounded-lg border border-border p-1 text-xs">
              <button
                type="button"
                onClick={() => setGuestPaymentMethod('stripe')}
                className={`rounded px-3 py-1 ${
                  guestPaymentMethod === 'stripe' ? 'bg-primary text-white' : 'text-foreground/70'
                }`}
              >
                Pay online (iDEAL/card)
              </button>
              <button
                type="button"
                onClick={() => setGuestPaymentMethod('cash')}
                className={`rounded px-3 py-1 ${
                  guestPaymentMethod === 'cash' ? 'bg-primary text-white' : 'text-foreground/70'
                }`}
              >
                Pay cash
              </button>
            </div>
          )}
          {error && (
            <p aria-live="assertive" className="mt-3 text-sm text-red-600">
              {error}
            </p>
          )}
          {success && (
            <p aria-live="polite" className="mt-3 text-sm text-green-700">
              {success}
            </p>
          )}
          <button
            type="button"
            onClick={handleBook}
            disabled={submitting || loading}
            className="mt-4 rounded-lg bg-primary px-5 py-2 text-sm font-semibold text-white disabled:opacity-60"
          >
            {submitting ? 'Booking...' : 'Book lesson'}
          </button>
        </section>
      </div>
      {showPackageModal && (
        <div className="fixed inset-0 z-[120] flex items-end justify-center bg-black/50 p-3 md:items-center">
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="package-modal-title"
            className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white p-5"
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <h3
                  id="package-modal-title"
                  className="font-heading text-xl font-semibold text-primary"
                >
                  Top up your credits
                </h3>
                <p className="mt-1 text-sm text-foreground/70">
                  Choose a package for {selectedLessonType?.duration_minutes} minute lessons.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowPackageModal(false)}
                aria-label="Close package selection"
                className="rounded border border-border px-2 py-1 text-xs"
              >
                Close
              </button>
            </div>
            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              {selectedDurationPackages
                .filter((entry) => entry.account_required)
                .map((entry) => renderPackageCard(entry))}
            </div>
            <p className="mt-3 text-xs text-foreground/60">
              Packages are account-only and credits are added immediately after successful payment.
            </p>
          </div>
        </div>
      )}
    </main>
  );
}
